import React, { useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { useParams as useBehaviorParams, useAuthorization } from 'hooks';
import { usePartnerType } from 'hooks/document/usePartnerType';
import { useTranslation } from 'react-i18next';
import { get, set } from '@totalsoft/change-tracking-react';
import { Box, Grid } from '@mui/material';
import { Info, WarningRounded, Policy, Edit } from '@mui/icons-material';
import { CollapseCard, Button, IconButton } from '@totalsoft/rocket-ui';
import { DateTime } from '@ts-private/rocket-extensions';
import AssetOfferDataCard from './AssetOfferDataCard';
import PartnerOfferDataCard from './PartnerOfferDataCard';
import OfferDataSummary from './OfferDataSummary';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import InsuranceDataContext from 'features/insuranceDocument/stepper/insuranceDataContext';
import AuthorizationCodes from 'features/common/constants/authorizationCodes';
import { PolicyOfferQuotationsProcessingText, PolicyOfferQuotationsWarningText } from '../PolicyStyles';

const OfferDataCard = ({
  insuranceDataLens,
  insuranceTypeCode,
  rcaInsuranceTypeCode,
  cascoInsuranceTypeCode,
  assetLens,
  assetLoading,
  category,
  subcategory,
  saving,
  saveDisabled,
  showEditableFields,
  insuredLens,
  insuredLoading,
  insuredId,
  clientLens,
  clientLoading,
  clientId,
  disabledStatus,
  validation,
  warningOnEditButton,
  documentDataHeader,
  onSave,
  onToggleShowEditableFields
}) => {
  const { t } = useTranslation();
  const { globalReadOnly, documentTypeId, documentStateId } = useContext(InsuranceDataContext);
  const hasRightToEditOfferData = useAuthorization(AuthorizationCodes.hasRightToEditOfferData, documentTypeId, documentStateId);
  const { isPF, isPFA, isPJ } = usePartnerType();

  const recomputeQuotationsDocumentStateCode = useBehaviorParams('RecomputeQuotationsDocumentStateCode');

  const readOnly = useCallback(
    (controlName, hasAuthorization) =>
      globalReadOnly({
        controlName,
        checkQuotationsStatus: { processingStatus: true, expiredStatus: true },
        hasAuthorization
      }),
    [globalReadOnly]
  );

  return (
    <CollapseCard
      icon={Policy}
      title={t('Offer.Edit.OfferData.Title')}
      defaultExpanded={false}
      subheader={
        <OfferDataSummary
          assetDescription={assetLens.description |> get}
          assetLoading={assetLoading}
          insuredLens={insuredLens}
          insuredLoading={insuredLoading}
          clientLens={clientLens}
          clientLoading={clientLoading}
          isPF={isPF(insuredLens.partnerTypeId |> get)}
          insuredIsClient={insuredId === clientId}
        />
      }
      hideSubheaderOnExpand={true}
      toggleOnHeaderClick
    >
      <Grid container px={1}>
        <Grid item container spacing={2}>
          <Grid container item xs={12} sm={6} alignItems={'center'}>
            <strong> {t('Offer.Edit.OfferData.StartDate')}:</strong>
            <Box ml={1}>
              <DateTime
                id='Insurance_StartDate'
                onChange={insuranceDataLens.startDate |> set}
                value={insuranceDataLens.startDate |> get}
                disabled={readOnly('PolicyOfferData.StartDate', hasRightToEditOfferData) || disabledStatus.isGenerationFlowBlocked}
                disablePast
                timeFormat={false}
                fullWidth
                required
                error={!isValid(validation.startDate)}
                helperText={getErrors(validation.startDate)}
              />
            </Box>
          </Grid>
        </Grid>
        <PartnerOfferDataCard
          partnerLens={insuredLens}
          title={insuredId === clientId ? `Offer.Edit.OfferData.Partner.InsuredIsClient` : `Offer.Edit.OfferData.Partner.Insured`}
          showEditableFields={showEditableFields}
          disabled={
            disabledStatus?.isInsuredAddressCompleted ||
            (insuredLens.isSystem |> get) ||
            Boolean(insuredLens.chPartnerId |> get) ||
            disabledStatus.isGenerationFlowBlocked
          }
          readOnly={readOnly}
          validation={validation}
          validationKey={'insured'}
          loading={insuredLoading}
          isPF={isPF(insuredLens.partnerTypeId |> get)}
          isPJ={isPJ(insuredLens.partnerTypeId |> get)}
          isPFA={isPFA(insuredLens.partnerTypeId |> get)}
        />
        {insuredId !== clientId && (
          <PartnerOfferDataCard
            partnerLens={clientLens}
            title={`Offer.Edit.OfferData.Partner.User`}
            showEditableFields={showEditableFields}
            disabled={
              disabledStatus?.isClientAddressCompleted ||
              (clientLens.isSystem |> get) ||
              Boolean(clientLens.chPartnerId |> get) ||
              disabledStatus.isGenerationFlowBlocked
            }
            readOnly={readOnly}
            validation={validation}
            validationKey={'client'}
            loading={clientLoading}
            isPF={isPF(clientLens.partnerTypeId |> get)}
            isPJ={isPJ(clientLens.partnerTypeId |> get)}
            isPFA={isPFA(clientLens.partnerTypeId |> get)}
          />
        )}
        <AssetOfferDataCard
          insuranceTypeCode={insuranceTypeCode}
          rcaInsuranceTypeCode={rcaInsuranceTypeCode}
          cascoInsuranceTypeCode={cascoInsuranceTypeCode}
          assetLens={assetLens}
          category={category}
          subcategory={subcategory}
          showEditableFields={showEditableFields}
          disabled={disabledStatus?.isAssetCompleted || disabledStatus.isGenerationFlowBlocked}
          readOnly={readOnly}
          validation={validation}
          loading={assetLoading}
        />

        <Grid item container xs={12} justifyContent={'space-between'} alignItems='center'>
          <Grid item alignItems={'center'}>
            <Grid container>
              {disabledStatus.isGenerationFlowBlocked ? (
                <PolicyOfferQuotationsWarningText>
                  <WarningRounded />
                  <span>{t('Validations.Generic.NegotiatedQuotationSelectedWarning')}</span>
                </PolicyOfferQuotationsWarningText>
              ) : (
                <>
                  <Button
                    color='primary'
                    onClick={onSave}
                    disabled={saveDisabled || readOnly('PolicyOfferData', hasRightToEditOfferData)}
                    id='OfferData_saveBtn'
                  >
                    {saving ? t('General.Saving') : t('General.Save')}
                  </Button>
                  {warningOnEditButton && (
                    <>
                      <WarningRounded />
                      <span>{t('Offer.Edit.OfferData.Actions.NeedToCompleteAllInfo')}</span>
                    </>
                  )}
                </>
              )}
              {documentDataHeader?.documentHeader?.documentStateCode === recomputeQuotationsDocumentStateCode && (
                <PolicyOfferQuotationsProcessingText>
                  <Info />
                  <span>{t('Offer.Edit.OfferData.Actions.ProcessingQuotations')}</span>
                </PolicyOfferQuotationsProcessingText>
              )}
            </Grid>
          </Grid>
          {insuranceTypeCode === rcaInsuranceTypeCode && !disabledStatus.fullCompleted && (
            <Grid item>
              <IconButton
                id='OfferData_editBtn'
                color={warningOnEditButton ? 'error' : 'primary'}
                size='small'
                tooltip={t('Tooltips.Edit')}
                onClick={onToggleShowEditableFields}
                disabled={readOnly('PolicyOfferData', hasRightToEditOfferData) || disabledStatus.isGenerationFlowBlocked}
              >
                <Edit fontSize={'small'} />
              </IconButton>
            </Grid>
          )}
        </Grid>
      </Grid>
    </CollapseCard>
  );
};

OfferDataCard.propTypes = {
  insuranceDataLens: PropTypes.object,
  insuranceTypeCode: PropTypes.string,
  rcaInsuranceTypeCode: PropTypes.string,
  cascoInsuranceTypeCode: PropTypes.string,
  assetLens: PropTypes.object,
  assetLoading: PropTypes.bool,
  category: PropTypes.string,
  subcategory: PropTypes.string,
  saving: PropTypes.bool,
  saveDisabled: PropTypes.bool,
  showEditableFields: PropTypes.bool,
  insuredLens: PropTypes.object,
  insuredLoading: PropTypes.bool,
  insuredId: PropTypes.number,
  clientLens: PropTypes.object,
  clientLoading: PropTypes.bool,
  clientId: PropTypes.number,
  disabledStatus: PropTypes.object,
  validation: PropTypes.object,
  warningOnEditButton: PropTypes.bool,
  documentDataHeader: PropTypes.object,
  onSave: PropTypes.func,
  onToggleShowEditableFields: PropTypes.func
};

export default OfferDataCard;
